import { Dictionary } from 'lodash';
import { Moment } from 'moment';
import { Team, User } from 'api/models';
import { ResourceType } from 'api/resource-management/types';
import { AllocationEvent } from 'components/organisms/resource-management/event-types';
import { allocationForProjectAsEvent } from 'components/organisms/resource-management/utils/events-conversion/allocations-by-projects';
import { WorkingDaysConfiguration } from 'components/organisms/user-capacity';
import { TemplateAllocation } from 'data/templates/projects';
import { defaultFormat } from 'utils/date-field/formatter';

interface Config {
  relativeDate: Moment;
  userLookup: Dictionary<User>;
  teamLookup: Dictionary<Team>;
  projectId: number;
  projectPhaseLookup: Dictionary<{
    id: number;
    name: string;
  }>;
  workingDaysConfig: WorkingDaysConfiguration;
}

export const asAllocationEvent =
  ({
    relativeDate,
    userLookup,
    teamLookup,
    projectId,
    projectPhaseLookup,
    workingDaysConfig,
  }: Config) =>
  (allocations: TemplateAllocation[]): AllocationEvent[] =>
    allocations
      .map((allocation) => {
        const startDate = relativeDate
          .startOf('day')
          .add(allocation.startDay, 'day');
        const endDate = startDate
          .add(allocation.duration - 1, 'days')
          .endOf('day');
        const commomProps = {
          id: allocation.id,
          startDate: defaultFormat(startDate),
          endDate: defaultFormat(endDate),
          minutes: allocation.minutes,
          projectPhase:
            allocation.projectPhaseId != null
              ? projectPhaseLookup[allocation.projectPhaseId]
              : undefined,
          billable: allocation.billable,
          medium: allocation.medium,
        };
        const { sources } = allocation;

        if (allocation.type === ResourceType.User) {
          const user = userLookup[allocation.userId];

          if (user == null) {
            return null;
          }

          return {
            ...allocationForProjectAsEvent(
              {
                ...commomProps,
                type: ResourceType.User,
                user,
              },
              projectId,
              [],
              {
                excludeNonWorkingDays: true,
                workingDaysConfig,
              },
            ),
            sources,
          };
        }
        const team = teamLookup[allocation.teamId];

        if (team == null) {
          return null;
        }

        return {
          ...allocationForProjectAsEvent(
            {
              ...commomProps,
              type: ResourceType.Team,
              team,
            },
            projectId,
            [],
            {
              excludeNonWorkingDays: true,
              workingDaysConfig,
            },
          ),
          sources,
        };
      })
      .filter(Boolean) as AllocationEvent[];
